/**
 * Simplified frontend cron pattern validation.
 * Validates basic structure: 5 fields with valid cron characters.
 * Backend performs authoritative validation with cron-parser.
 */
export const isValidCronPattern = (pattern: string): boolean => {
  const trimmed = pattern.trim();
  if (!trimmed) return false;
  const parts = trimmed.split(/\s+/);
  if (parts.length !== 5) return false;
  // Each part should only contain valid cron characters:
  // digits, *, /, -, , and combinations like */6, 1-5, 0,15,30
  const cronFieldRegex = /^(\*|[0-9]+|[0-9]+-[0-9]+|[0-9]+\/[0-9]+|\*\/[0-9]+|[0-9]+(,[0-9]+)*)$/;
  return parts.every(part => cronFieldRegex.test(part));
};

/**
 * Returns human-readable description of cron pattern.
 * Covers common patterns; complex patterns show generic message.
 */
export const describeCronPattern = (pattern: string): string | null => {
  if (!isValidCronPattern(pattern)) return null;
  const parts = pattern.trim().split(/\s+/);
  const [minute, hour, , , dayOfWeek] = parts;

  // Every N hours patterns: "0 */N * * *"
  if (minute === "0" && hour.startsWith("*/")) {
    const hours = parseInt(hour.slice(2), 10);
    if (hours === 1) return "Co godzinę";
    return `Co ${hours} godzin${hours >= 5 ? "" : "y"}`;
  }

  // Daily at specific hour: "0 H * * *"
  if (minute === "0" && /^\d+$/.test(hour) && dayOfWeek === "*") {
    return `Codziennie o ${hour}:00`;
  }

  // Weekdays at specific hour: "0 H * * 1-5"
  if (minute === "0" && /^\d+$/.test(hour) && dayOfWeek === "1-5") {
    return `W dni robocze o ${hour}:00`;
  }

  // Hour range: "0 6-20 * * *"
  if (minute === "0" && /^\d+-\d+$/.test(hour) && dayOfWeek === "*") {
    const [start, end] = hour.split("-");
    return `Codziennie od ${start}:00 do ${end}:00 (co godzinę)`;
  }

  return null; // Complex pattern - no description
};
